import { useMemo, useState } from 'react'
import { usePeriodo } from '../context/PeriodoContext'
import { useAuth } from '../context/AuthContext'
import { useQuery } from 'react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { endpoints } from '../services/cfoApi'
import PageInsights from '../components/agents/PageInsights'
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  ChartPieIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UsersIcon,
  ReceiptPercentIcon,
  BuildingOffice2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'
import {
  ResponsiveContainer, ComposedChart, LineChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts'
import { PeriodoActivo } from '../components/common/FiltroPeriodo'
import TabCategorias from '../components/ventas/TabCategorias'
import { Leyenda } from '../components/common/leyendas'
import TabMatriz from '../components/ventas/TabMatriz'
import FiltroProducto, { FILTRO_VACIO } from '../components/ventas/FiltroProducto'
import { exportarExcel, traerTodo } from '../services/excel'

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------
const fmtQ    = (n) => `Q${Math.round(Number(n) || 0).toLocaleString('es-GT')}`
const fmtM    = (n) => {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1e6) return `Q${(v / 1e6).toFixed(1)}M`
  if (Math.abs(v) >= 1e3) return `Q${Math.round(v / 1e3)}k`
  return fmtQ(v)
}
const fmtNum  = (n) => Number(n || 0).toLocaleString('es-GT', { maximumFractionDigits: 2 })
const fmtInt  = (n) => Number(n || 0).toLocaleString('es-GT')
const fmtPct  = (n, d = 1) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(d)}%`)
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—')
const fmtPeriod = (p) => {
  if (!p) return ''
  const [y, m] = p.split('-')
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
  return `${meses[+m - 1]} ${y.slice(2)}`
}

const margenTone = (pct) => {
  if (pct === null || pct === undefined) return 'text-[var(--text-muted)]'
  if (pct >= 40) return 'text-[var(--success)]'
  if (pct >= 25) return 'text-[var(--warning)]'
  return 'text-[var(--danger)]'
}

// Aviso de ventas sin costo en el ERP: no entran al margen %. Solo se muestra si pesa.
const SinCosto = ({ pct }) => (pct >= 1 ? (
  <p className="text-[10px] font-normal text-[var(--text-muted)] whitespace-nowrap" title="El margen % se calcula solo con las líneas que traen costo en el ERP">
    {Number(pct).toFixed(1)}% de ventas sin costo <Leyenda k="pct_sin_costo" />
  </p>
) : null)

// Variación vs período anterior
const Var = ({ pct, nuevo }) => {
  if (nuevo) return <span className="text-xs font-semibold text-[var(--success)]">nuevo</span>
  if (pct === null || pct === undefined) return <span className="text-[var(--text-muted)]">—</span>
  const tone = pct > 0 ? 'text-[var(--success)]' : pct < 0 ? 'text-[var(--danger)]' : 'text-[var(--text-muted)]'
  return <span className={`tabular-nums font-semibold ${tone}`}>{pct > 0 ? '+' : ''}{Number(pct).toFixed(1)}%</span>
}

// Columnas del Excel de líneas de venta
const COLUMNAS_VENTAS = [
  { label: 'Fecha', get: f => String(f.fecha_emision).slice(0, 10) },
  { label: 'Documento', get: f => `${f.tipo_doc} ${f.fact_num}` },
  { label: 'Código cliente', get: f => f.codigo_cliente },
  { label: 'Cliente', get: f => f.cliente },
  { label: 'Vendedor', get: f => f.vendedor },
  { label: 'Sucursal', get: f => f.sucursal },
  { label: 'Código artículo', get: f => f.codigo_articulo },
  { label: 'Artículo', get: f => f.articulo },
  { label: 'Subcategoría', get: f => f.linea },
  { label: 'Sublínea', get: f => f.sublinea },
  { label: 'Unidades', get: f => f.unidades },
  { label: 'Total sin IVA', get: f => f.total_sin_iva.toFixed(2) },
  { label: 'IVA', get: f => f.iva.toFixed(2) },
  { label: 'Total con IVA', get: f => f.total_con_iva.toFixed(2) },
  { label: 'Costo', get: f => (f.sin_costo || f.costo_total_facturado == null ? '' : f.costo_total_facturado.toFixed(2)) },
  { label: 'Margen bruto', get: f => (f.margen_bruto === null ? '' : f.margen_bruto.toFixed(2)) },
  { label: 'Margen %', get: f => (f.margen_bruto_pct === null ? '' : Number(f.margen_bruto_pct).toFixed(1)) },
]

const PIE_COLORS = ['#001639', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444', '#84cc16', '#f97316', '#a855f7']

// -------------------------------------------------------------------
// Página
// -------------------------------------------------------------------
export default function Ventas() {
  const { desde, hasta } = usePeriodo()
  // ?tab=vendedores al volver desde la ficha de un vendedor
  const [searchParams] = useSearchParams()
  const [tab, setTab]             = useState(searchParams.get('tab') || 'resumen')
  const [busqueda, setBusqueda]   = useState('')
  const [clienteSel, setCliente]  = useState('')
  const [vendedorSel, setVendedor] = useState('')
  // Filtro de producto (Categoría › Subcategoría › Sublínea), común a todas las pestañas
  const [producto, setProducto]   = useState(FILTRO_VACIO)
  const prodParams = Object.fromEntries(Object.entries(producto).filter(([, v]) => v))
  const prodKey = JSON.stringify(prodParams)

  const commonParams = { desde, hasta }

  const { data: rRes, isLoading: loadingR, isPreviousData: rViejo } = useQuery(
    ['ventas-resumen', desde, hasta, prodKey],
    () => endpoints.ventas.resumen({ ...commonParams, ...prodParams }),
    { keepPreviousData: true }
  )
  const { data: cRes, isLoading: cCarga, isPreviousData: cViejo } = useQuery(
    ['ventas-clientes', desde, hasta, prodKey],
    () => endpoints.ventas.clientes({ ...commonParams, ...prodParams, limit: 30 }),
    { keepPreviousData: true }
  )
  const { data: vRes, isLoading: vCarga, isPreviousData: vViejo } = useQuery(
    ['ventas-vendedores', desde, hasta, prodKey],
    () => endpoints.ventas.vendedores({ ...commonParams, ...prodParams }),
    { keepPreviousData: true }
  )
  const { data: aRes } = useQuery(
    ['ventas-articulos', desde, hasta, prodKey],
    () => endpoints.ventas.articulos({ ...commonParams, ...prodParams, limit: 15 }),
    { keepPreviousData: true }
  )
  const { data: svRes } = useQuery(
    ['ventas-serie-vend', desde, hasta, prodKey],
    () => endpoints.ventas.serieVendedores({ ...commonParams, ...prodParams, limit: 5 }),
    { keepPreviousData: true, enabled: tab === 'vendedores' }
  )
  const { data: dRes, isFetching: fetchingDetalle } = useQuery(
    ['ventas-detalle', desde, hasta, busqueda, clienteSel, vendedorSel, prodKey],
    () => endpoints.ventas.detalle({
      ...commonParams, ...prodParams, busqueda, codigo_cliente: clienteSel, vendedor: vendedorSel,
      limit: 300, offset: 0,
    }),
    { keepPreviousData: true, enabled: tab === 'resumen' }
  )

  const r          = rRes?.data || {}
  const serie      = r.serie_mensual || []
  const clientes   = cRes?.data?.clientes || []
  const vendedores = vRes?.data?.vendedores || []
  const articulos  = aRes?.data?.articulos || []
  const serieVend  = svRes?.data
  const filas      = dRes?.data?.filas || []
  const totalFilas   = dRes?.data?.total_filas || 0
  const sumaFiltrada = dRes?.data?.suma_ventas || 0

  // Exporta las líneas de venta con los filtros activos (período, producto, búsqueda, cliente, vendedor)
  const [exportando, setExportando] = useState(false)
  const exportar = async () => {
    setExportando(true)
    try {
      const lineas = await traerTodo(endpoints.ventas.detalle, {
        ...commonParams, ...prodParams, busqueda, codigo_cliente: clienteSel, vendedor: vendedorSel,
      })
      await exportarExcel(`ventas_${desde}_${hasta}.xlsx`, COLUMNAS_VENTAS, lineas, { hoja: 'Ventas' })
    } finally {
      setExportando(false)
    }
  }


  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Link to="/" className="w-10 h-10 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] flex items-center justify-center transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-[var(--text-muted)]" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#001639] flex items-center justify-center">
              <ArrowTrendingUpIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Ventas</h1>
              <p className="text-sm text-[var(--text-muted)]">
                {loadingR || rViejo ? 'Cargando…' :
                  `${fmtInt(r.facturas)} facturas · ${fmtInt(r.clientes)} clientes · ${fmtInt(r.vendedores)} vendedores activos`}
              </p>
              <PeriodoActivo className="mt-1" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportar} disabled={exportando} className="btn-secondary flex items-center gap-2 disabled:opacity-60">
            <ArrowDownTrayIcon className="w-4 h-4" />
            {exportando ? 'Exportando…' : 'Exportar Excel'}
          </button>
        </div>
      </div>

      {/* Navegación: pestañas + filtro de producto (aplica a todo lo de abajo) */}
      <div className="space-y-3">
      <div className="flex gap-1 border-b border-[var(--border-default)] overflow-x-auto">
        {[
          { id: 'resumen',   label: 'Resumen',     icon: ChartBarIcon },
          { id: 'categorias',label: 'Categorías',  icon: Squares2X2Icon },
          { id: 'vendedores',label: 'Vendedores',  icon: UsersIcon },
          { id: 'clientes',  label: 'Clientes',    icon: UserGroupIcon },
          { id: 'matriz',    label: 'Matriz',      icon: TableCellsIcon },
        ].map(t => {
          const Icon = t.icon
          const active = tab === t.id
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                active
                  ? 'border-[#001639] text-[var(--text-primary)]'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
              }`}
            >
              <Icon className="w-4 h-4" />{t.label}
            </button>
          )
        })}
      </div>
      <FiltroProducto value={producto} onChange={setProducto} />
      </div>

      {/* KPIs (respetan período y filtro de producto) */}
      <KPIs r={r} loading={loadingR || rViejo} />

      {/* Content por tab */}
      {tab === 'resumen'    && (
        <PageInsights vertical="ventas" maxInsights={4} />
      )}
      {tab === 'resumen'    && (
        <TabResumen
          serie={serie} articulos={articulos} filas={filas} totalFilas={totalFilas}
          sumaFiltrada={sumaFiltrada} fetchingDetalle={fetchingDetalle}
          busqueda={busqueda} setBusqueda={setBusqueda}
          clienteSel={clienteSel} vendedorSel={vendedorSel}
          setCliente={setCliente} setVendedor={setVendedor}
        />
      )}
      {['vendedores', 'clientes'].includes(tab) && (tab === 'vendedores' ? vCarga || vViejo : cCarga || cViejo) && (
        <div className="space-y-4">
          <div className="h-16 rounded-lg animate-pulse bg-[var(--bg-tertiary)]" />
          <div className="card h-72 animate-pulse bg-[var(--bg-secondary)]" />
        </div>
      )}
      {tab === 'vendedores' && !(vCarga || vViejo) && (
        <TabVendedores vendedores={vendedores} serieVend={serieVend} resumen={vRes?.data} />
      )}
      {tab === 'categorias' && <TabCategorias filtro={producto} onFiltro={setProducto} />}
      {tab === 'matriz'     && <TabMatriz filtroProducto={prodParams} />}
      {tab === 'clientes'   && !(cCarga || cViejo) && (
        <TabClientes clientes={clientes} resumen={cRes?.data} />
      )}

      <p className="text-xs text-[var(--text-muted)] italic">
        {r.metodologia ? <>Margen sobre costo de compras: ventas sin IVA menos {r.metodologia.unidad === 'millar' ? 'millares' : 'kilos'} vendidos por CPP. Compras hasta {r.metodologia.corte_cpp}; ventas hasta {r.metodologia.hasta_efectivo}. Las ventas sin CPP se excluyen del margen. Mes abierto: ventas visibles y CPP pendiente. <Leyenda k="margen_mp" /></> : <>Laminados, Empaques, Tapas y válvulas e Inyectables usan CPP de compras de meses cerrados. Las otras líneas conservan el costo del ERP. Las ventas sin CPP quedan fuera del margen.</>}
      </p>
    </div>
  )
}

// -------------------------------------------------------------------
// KPIs globales
// -------------------------------------------------------------------
function KPIs({ r, loading }) {
  // Mientras carga no se muestran ceros ni números del filtro anterior
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {['Ventas (sin IVA)', 'Margen bruto', 'Clientes que compraron', 'Ticket promedio'].map(l => (
          <div key={l} className="kpi-card">
            <span className="kpi-label">{l}</span>
            <span className="block h-7 w-24 mt-1 rounded bg-[var(--bg-tertiary)] animate-pulse" />
            <span className="block h-3 w-32 mt-2 rounded bg-[var(--bg-tertiary)] animate-pulse" />
          </div>
        ))}
      </div>
    )
  }
  const v = (x) => x
  // Asistente de Ventas: sin márgenes (el backend no los manda)
  const { oculta } = useAuth()
  const sinMargen = oculta('/ventas', 'margen')
  const deltaMargen = r.margen_bruto_pct != null && r.margen_pct_prev != null ? r.margen_bruto_pct - r.margen_pct_prev : null
  const deltaClientes = r.clientes_prev ? r.clientes - r.clientes_prev : null
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="kpi-card">
        <span className="kpi-label flex items-center gap-1">Ventas (sin IVA) <Leyenda k="ventas" /></span>
        <p className="kpi-value">{v(fmtM(r.ventas_sin_iva))}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          <Var pct={r.variacion_pct} /> vs {fmtM(r.ventas_prev)} año anterior <Leyenda k="vs_anio_anterior" />
        </p>
      </div>
      {!sinMargen && (
      <div className="kpi-card">
        <span className="kpi-label flex items-center gap-1">{r.metodologia ? 'Margen sobre materia prima' : 'Margen bruto'} <Leyenda k={r.metodologia ? 'margen_mp' : 'margen_bruto'} /></span>
        <p className={`kpi-value ${margenTone(r.margen_bruto_pct)}`}>{v(fmtPct(r.margen_bruto_pct))}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {r.margen_bruto === null ? 'N/D' : fmtM(r.margen_bruto)}
          {deltaMargen !== null && (
            <> · <span className={deltaMargen >= 0 ? 'text-[var(--success)]' : 'text-[var(--danger)]'}>{deltaMargen >= 0 ? '+' : ''}{deltaMargen.toFixed(1)} pts</span> vs año ant. <Leyenda k="margen_pts" /></>
          )}
        </p>
        <SinCosto pct={r.pct_ventas_sin_costo} />
      </div>
      )}
      <div className="kpi-card">
        <span className="kpi-label flex items-center gap-1">Clientes que compraron <Leyenda k="clientes_compraron" /></span>
        <p className="kpi-value">{v(fmtInt(r.clientes))}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {deltaClientes === null ? '—' : <>{deltaClientes >= 0 ? '+' : ''}{deltaClientes} vs año anterior ({fmtInt(r.clientes_prev)})</>}
        </p>
      </div>
      <div className="kpi-card">
        <span className="kpi-label flex items-center gap-1">Ticket promedio <Leyenda k="ticket_promedio" /></span>
        <p className="kpi-value">{v(fmtM(r.ticket_promedio))}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">{fmtInt(r.facturas)} facturas</p>
      </div>
    </div>
  )
}

// -------------------------------------------------------------------
// TAB: RESUMEN
// -------------------------------------------------------------------
function TabResumen({
  serie, articulos, filas, totalFilas, sumaFiltrada, fetchingDetalle,
  busqueda, setBusqueda, clienteSel, vendedorSel, setCliente, setVendedor,
}) {
  return (
    <div className="space-y-6">
      {/* Serie mensual: ventas + margen */}
      <div className="card">
        <div className="section-header">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-[var(--text-muted)]" />
            <h2 className="font-semibold">Ventas y margen mensual</h2>
            <Leyenda k="serie_mensual" />
          </div>
          <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#001639] rounded-sm" /> Ventas</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-500 rounded-sm" /> Margen</span>
            <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-amber-500" /> Margen %</span>
          </div>
        </div>
        <div className="p-5 pt-0">
          {serie.length === 0 ? (
            <p className="py-10 text-center text-sm text-[var(--text-muted)]">Sin datos.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={serie} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                <XAxis dataKey="periodo" tickFormatter={fmtPeriod} tick={{ fontSize: 12 }} stroke="var(--text-muted)" />
                <YAxis yAxisId="q" tickFormatter={(v) => `Q${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 12 }} stroke="var(--text-muted)" width={70} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} stroke="var(--text-muted)" width={45} />
                <Tooltip
                  formatter={(v, k) => k === 'margen_pct' ? `${Number(v).toFixed(1)}%` : fmtQ(v)}
                  labelFormatter={fmtPeriod}
                  contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                />
                <Bar  yAxisId="q"   dataKey="ventas" fill="#001639" radius={[6,6,0,0]} />
                <Bar  yAxisId="q"   dataKey="margen" fill="#10b981" radius={[6,6,0,0]} />
                <Line yAxisId="pct" dataKey="margen_pct" stroke="#f59e0b" strokeWidth={2} dot={false} name="Margen %" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top artículos */}
      <div className="card">
        <div className="section-header">
          <CubeIcon className="w-5 h-5 text-[var(--text-muted)]" />
          <h2 className="font-semibold">Top 15 artículos por facturación</h2>
          <Leyenda k="margen_bruto" />
        </div>
        <div className="p-5 pt-0 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-[var(--text-muted)] uppercase">
                <th className="text-left  font-semibold pb-2">Artículo</th>
                <th className="text-left  font-semibold pb-2">Línea</th>
                <th className="text-right font-semibold pb-2">Clientes</th>
                <th className="text-right font-semibold pb-2">Unidades</th>
                <th className="text-right font-semibold pb-2">Ventas</th>
                <th className="text-right font-semibold pb-2">Margen</th>
                <th className="text-right font-semibold pb-2">Margen%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]">
              {articulos.map(a => (
                <tr key={a.codigo} className="text-sm hover:bg-[var(--bg-secondary)]">
                  <td className="py-2 pr-3">
                    <p className="font-medium nombre-articulo max-w-[320px]">{a.descripcion}</p>
                    <p className="text-xs text-[var(--text-muted)]">{a.codigo}</p>
                  </td>
                  <td className="py-2 pr-3 text-[var(--text-secondary)]">
                    <p>{a.linea}</p>
                    {a.sublinea && <p className="text-xs text-[var(--text-muted)]">{a.sublinea}</p>}
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmtInt(a.clientes)}</td>
                  <td className="py-2 text-right tabular-nums">{fmtNum(a.unidades)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">{fmtM(a.ventas)}</td>
                  <td className="py-2 text-right tabular-nums">{fmtM(a.margen)}</td>
                  <td className={`py-2 text-right tabular-nums font-semibold ${margenTone(a.margen_pct)}`}>
                    {a.margen_pct !== null ? `${a.margen_pct.toFixed(1)}%` : '—'}
                    <SinCosto pct={a.pct_sin_costo} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filtros y detalle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-5 h-5 text-[var(--text-muted)] absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar por cliente, artículo, factura o FEL…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input w-full pl-12"
          />
        </div>
        {(clienteSel || vendedorSel) && (
          <button onClick={() => { setCliente(''); setVendedor('') }} className="btn-secondary">
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="p-4 border-b border-[var(--border-default)] flex items-center justify-between bg-[var(--bg-secondary)] flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ReceiptPercentIcon className="w-5 h-5 text-[var(--text-muted)]" />
            <span className="text-sm text-[var(--text-muted)]">
              {fetchingDetalle ? 'Actualizando…' : `${fmtInt(filas.length)} de ${fmtInt(totalFilas)} líneas`}
              {clienteSel && <span className="ml-2 badge-warning">{clienteSel}</span>}
              {vendedorSel && <span className="ml-2 badge-warning">{vendedorSel}</span>}
            </span>
          </div>
          <span className="text-sm font-semibold">Total filtrado sin IVA: {fmtM(sumaFiltrada)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-default)]">
              <tr>
                <th className="px-3 py-3 text-left  text-xs font-semibold text-[var(--text-muted)] uppercase">Fecha</th>
                <th className="px-3 py-3 text-left  text-xs font-semibold text-[var(--text-muted)] uppercase">Factura</th>
                <th className="px-3 py-3 text-left  text-xs font-semibold text-[var(--text-muted)] uppercase">Cliente</th>
                <th className="px-3 py-3 text-left  text-xs font-semibold text-[var(--text-muted)] uppercase">Vendedor</th>
                <th className="px-3 py-3 text-left  text-xs font-semibold text-[var(--text-muted)] uppercase">Artículo</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-[var(--text-muted)] uppercase">Unid.</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-[var(--text-muted)] uppercase">Sin IVA</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-[var(--text-muted)] uppercase">Margen% <Leyenda k="margen_bruto" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]">
              {filas.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                    {fetchingDetalle ? 'Cargando…' : 'Sin líneas que coincidan con el filtro.'}
                  </td>
                </tr>
              )}
              {filas.map(f => (
                <tr key={f.id} className="hover:bg-[var(--bg-secondary)] transition-colors">
                  <td className="px-3 py-2 text-sm text-[var(--text-secondary)] tabular-nums whitespace-nowrap">{fmtDate(f.fecha_emision)}</td>
                  <td className="px-3 py-2 text-sm">
                    <p className="font-medium">{f.tipo_doc} {f.fact_num}</p>
                    {f.fel && <p className="text-xs text-[var(--text-muted)] truncate max-w-[120px]">FEL {f.fel}</p>}
                  </td>
                  <td className="px-3 py-2 text-sm max-w-[220px]">
                    <p className="font-medium truncate">{f.cliente}</p>
                    <p className="text-xs text-[var(--text-muted)]">{f.codigo_cliente}</p>
                  </td>
                  <td className="px-3 py-2 text-sm max-w-[160px]">
                    <p className="truncate text-[var(--text-secondary)]">{f.vendedor || '—'}</p>
                  </td>
                  <td className="px-3 py-2 text-sm max-w-[240px]">
                    <p className="nombre-articulo">{f.articulo}</p>
                    <p className="text-xs text-[var(--text-muted)]">{f.codigo_articulo}</p>
                  </td>
                  <td className="px-3 py-2 text-right text-sm tabular-nums">{fmtNum(f.unidades)}</td>
                  <td className="px-3 py-2 text-right text-sm tabular-nums font-semibold">{fmtM(f.total_sin_iva)}</td>
                  <td className={`px-3 py-2 text-right text-sm tabular-nums font-semibold ${margenTone(f.margen_bruto_pct)}`}>
                    {f.margen_bruto_pct !== null ? `${f.margen_bruto_pct.toFixed(1)}%` : (
                      <span className="text-xs font-normal text-[var(--text-muted)]" title="El ERP no trae costo para esta línea">sin costo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// -------------------------------------------------------------------
// TAB: VENDEDORES
// -------------------------------------------------------------------
function TabVendedores({ vendedores, serieVend, resumen }) {
  const top5 = vendedores.slice(0, 5)
  const topVendedor = vendedores[0]
  const topPct = topVendedor?.porcentaje || 0
  const clientesTotales = vendedores.reduce((s, v) => s + v.clientes, 0)

  return (
    <div className="space-y-6">
      {/* Insights */}
      {topVendedor && (
        <div className={`rounded-lg border p-4 flex items-start gap-3 ${
          topPct >= 40
            ? 'border-[var(--warning)] bg-[var(--warning-bg,#fff7ed)]'
            : 'border-[var(--border-default)] bg-[var(--bg-secondary)]'
        }`}>
          {topPct >= 40
            ? <ExclamationTriangleIcon className="w-5 h-5 text-[var(--warning)] flex-shrink-0 mt-0.5" />
            : <ArrowTrendingUpIcon className="w-5 h-5 text-[var(--text-muted)] flex-shrink-0 mt-0.5" />}
          <div className="text-sm">
            <p className="font-semibold">
              {topPct >= 40 ? '⚠ Concentración alta en un vendedor' : `Vendedor top: ${topVendedor.vendedor}`} <Leyenda k="vendedor_top" />
            </p>
            <p className="text-[var(--text-secondary)]">
              <strong>{topVendedor.vendedor}</strong> concentra el <strong>{fmtPct(topPct, 2)}</strong> de las ventas ({fmtM(topVendedor.ventas)})
              con {topVendedor.clientes} clientes {topVendedor.margen_pct !== null ? `y margen del ${fmtPct(topVendedor.margen_pct)}` : ''}.
              {topPct >= 40 ? ' Revisar plan de sucesión y redistribución de cartera.' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Serie mensual + Pie de participación */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Serie mensual top 5 vendedores */}
        <div className="card lg:col-span-2">
          <div className="section-header">
            <ChartBarIcon className="w-5 h-5 text-[var(--text-muted)]" />
            <h2 className="font-semibold">Evolución mensual de los top 5 vendedores</h2>
            <Leyenda k="serie_mensual" />
          </div>
          <div className="p-5 pt-0">
            {!serieVend || serieVend.serie?.length === 0 ? (
              <p className="py-10 text-center text-sm text-[var(--text-muted)]">Sin datos.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={serieVend.serie} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                  <XAxis dataKey="periodo" tickFormatter={fmtPeriod} tick={{ fontSize: 12 }} stroke="var(--text-muted)" />
                  <YAxis tickFormatter={(v) => `Q${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 12 }} stroke="var(--text-muted)" width={70} />
                  <Tooltip
                    formatter={(v) => fmtQ(v)}
                    labelFormatter={fmtPeriod}
                    contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {(serieVend.vendedores || []).map((v, i) => (
                    <Line key={v} type="monotone" dataKey={v} stroke={PIE_COLORS[i % PIE_COLORS.length]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Pie participación */}
        <div className="card lg:col-span-1">
          <div className="section-header">
            <ChartPieIcon className="w-5 h-5 text-[var(--text-muted)]" />
            <h2 className="font-semibold">Participación (top 5)</h2>
            <Leyenda k="participacion" />
          </div>
          <div className="p-5 pt-0">
            {top5.length === 0 ? (
              <p className="py-10 text-center text-sm text-[var(--text-muted)]">Sin datos.</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={top5.map(v => ({ name: v.vendedor, value: v.ventas }))}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={40}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {top5.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtM(v)} contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="space-y-1.5 mt-3">
              {top5.map((v, i) => (
                <div key={v.vendedor} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    {v.codigo
                      ? <Link to={`/ventas/vendedor/${encodeURIComponent(v.codigo)}`} className="truncate hover:underline">{v.vendedor}</Link>
                      : <span className="truncate">{v.vendedor}</span>}
                  </div>
                  <span className="tabular-nums font-semibold">{fmtPct(v.porcentaje, 2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ranking completo */}
      <div className="card">
        <div className="section-header">
          <UsersIcon className="w-5 h-5 text-[var(--text-muted)]" />
          <h2 className="font-semibold">Ranking de vendedores ({vendedores.length})</h2>
          {resumen && (
            <span className="ml-auto text-xs text-[var(--text-muted)]">
              Total {fmtM(resumen.total)} · <Var pct={resumen.variacion_pct} /> vs año ant.
            </span>
          )}
        </div>
        <div className="p-5 pt-0 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-[var(--text-muted)] uppercase">
                <th className="text-left  font-semibold pb-2">Vendedor</th>
                <th className="text-right font-semibold pb-2">Clientes</th>
                <th className="text-right font-semibold pb-2">Facturas</th>
                <th className="text-right font-semibold pb-2">Ventas</th>
                <th className="text-right font-semibold pb-2">Año ant. <Leyenda k="vs_anio_anterior" /></th>
                <th className="text-right font-semibold pb-2">Var. <Leyenda k="cambio" /></th>
                <th className="text-right font-semibold pb-2">Margen% año ant.</th>
                <th className="text-right font-semibold pb-2">Margen% <Leyenda k="margen_bruto" /></th>
                <th className="text-right font-semibold pb-2">Share <Leyenda k="participacion" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]">
              {vendedores.map((v, i) => (
                <tr key={v.vendedor} className="text-sm hover:bg-[var(--bg-secondary)]">
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-[var(--text-muted)] tabular-nums w-6">#{i + 1}</span>
                      <div className="min-w-0">
                        {v.codigo ? (
                          <Link to={`/ventas/vendedor/${encodeURIComponent(v.codigo)}`} title="Ver ficha del vendedor" className="font-medium truncate max-w-[220px] block hover:underline text-[var(--text-primary)]">
                            {v.vendedor}
                          </Link>
                        ) : (
                          <p className="font-medium truncate max-w-[220px]">{v.vendedor}</p>
                        )}
                        <p className="text-xs text-[var(--text-muted)]">{v.codigo || '—'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {fmtInt(v.clientes)}
                    {v.clientes_prev > 0 && v.clientes !== v.clientes_prev && (
                      <span className="text-[10px] text-[var(--text-muted)] ml-1">({v.clientes > v.clientes_prev ? '+' : ''}{v.clientes - v.clientes_prev})</span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmtInt(v.facturas)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">{fmtM(v.ventas)}</td>
                  <td className="py-2 text-right tabular-nums text-[var(--text-muted)]">{v.ventas_prev ? fmtM(v.ventas_prev) : '—'}</td>
                  <td className="py-2 text-right"><Var pct={v.variacion_pct} nuevo={v.nuevo} /></td>
                  <td className="py-2 text-right tabular-nums text-[var(--text-muted)]">{fmtPct(v.margen_pct_prev)}</td>
                  <td className={`py-2 text-right tabular-nums font-semibold ${margenTone(v.margen_pct)}`}>
                    {v.margen_pct !== null ? `${v.margen_pct.toFixed(1)}%` : '—'}
                  {v.margen_pct !== null && v.margen_pct_prev !== null && (
                    <p className="text-[10px] font-normal text-[var(--text-muted)]">{v.margen_pct - v.margen_pct_prev >= 0 ? '+' : ''}{(v.margen_pct - v.margen_pct_prev).toFixed(1)} pts</p>
                  )}
                  <SinCosto pct={v.pct_sin_costo} />
                  </td>
                  <td className={`py-2 text-right tabular-nums font-semibold ${v.porcentaje >= 20 ? 'text-[var(--warning)]' : 'text-[var(--text-secondary)]'}`}>
                    {fmtPct(v.porcentaje, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[var(--text-muted)] mt-3">
            Total clientes atendidos por el equipo (con duplicados entre vendedores): {fmtInt(clientesTotales)}.
          </p>
        </div>
      </div>
    </div>
  )
}

// -------------------------------------------------------------------
// TAB: CLIENTES
// -------------------------------------------------------------------
function TabClientes({ clientes, resumen }) {
  const totalVentas = resumen?.total || 0
  const [verPerdidos, setVerPerdidos] = useState(false)
  // Concentración pareto: acumulado por rank
  const paretoData = useMemo(() => {
    let acum = 0
    return clientes.map((c, i) => {
      acum += c.ventas
      return {
        rank: i + 1,
        cliente: c.cliente,
        ventas: c.ventas,
        acumulado: acum,
        acum_pct: totalVentas > 0 ? Math.min(100, Math.round(acum / totalVentas * 1000) / 10) : 0,
      }
    })
  }, [clientes, totalVentas])

  const top10Pct = paretoData[9]?.acum_pct || 0
  const top20Pct = paretoData[19]?.acum_pct || 0

  return (
    <div className="space-y-6">
      {/* Concentración y movimiento de cartera */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="kpi-card">
          <span className="kpi-label flex items-center gap-1">Top 10 clientes <Leyenda k="concentracion_top" /></span>
          <p className={`kpi-value ${top10Pct >= 50 ? 'text-[var(--warning)]' : 'text-[var(--text-primary)]'}`}>{top10Pct}%</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">del total facturado</p>
        </div>
        <div className="kpi-card">
          <span className="kpi-label flex items-center gap-1">Top 20 clientes <Leyenda k="concentracion_top" /></span>
          <p className="kpi-value">{top20Pct}%</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">del total facturado</p>
        </div>
        <div className="kpi-card">
          <span className="kpi-label flex items-center gap-1">Clientes activos <Leyenda k="clientes_activos" /></span>
          <p className="kpi-value">{fmtInt(resumen?.n_clientes)}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{fmtInt(resumen?.n_clientes_prev)} en el período anterior</p>
        </div>
        <div className="kpi-card">
          <span className="kpi-label flex items-center gap-1">Clientes nuevos <Leyenda k="clientes_nuevos" /></span>
          <p className="kpi-value text-[var(--success)]">{fmtInt(resumen?.n_nuevos)}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{fmtM(resumen?.venta_nuevos)} vendidos</p>
        </div>
        <div className="kpi-card">
          <span className="kpi-label flex items-center gap-1">Clientes perdidos <Leyenda k="clientes_perdidos" /></span>
          <p className="kpi-value text-[var(--danger)]">{fmtInt(resumen?.n_perdidos)}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{fmtM(resumen?.venta_perdida)} el período anterior</p>
        </div>
      </div>

      {/* Pareto: barras + línea acumulada */}
      <div className="card">
        <div className="section-header">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-[var(--text-muted)]" />
            <h2 className="font-semibold">Concentración Pareto (top 30 clientes)</h2>
            <Leyenda k="pareto" />
          </div>
          <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#001639] rounded-sm" /> Ventas</span>
            <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-amber-500" /> % acumulado</span>
          </div>
        </div>
        <div className="p-5 pt-0">
          {paretoData.length === 0 ? (
            <p className="py-10 text-center text-sm text-[var(--text-muted)]">Sin datos.</p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={paretoData} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                <XAxis dataKey="rank" tick={{ fontSize: 11 }} stroke="var(--text-muted)" label={{ value: 'Rank cliente', position: 'insideBottom', offset: -5, fontSize: 11 }} />
                <YAxis yAxisId="q" tickFormatter={(v) => `Q${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 12 }} stroke="var(--text-muted)" width={70} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} stroke="var(--text-muted)" width={45} />
                <Tooltip
                  formatter={(v, k) => k === 'acum_pct' ? `${Number(v).toFixed(1)}%` : fmtQ(v)}
                  labelFormatter={(l) => `Cliente #${l}`}
                  contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }}
                />
                <Bar yAxisId="q" dataKey="ventas" fill="#001639" radius={[3,3,0,0]} />
                <Line yAxisId="pct" dataKey="acum_pct" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Ranking clientes */}
      <div className="card">
        <div className="section-header">
          <UserGroupIcon className="w-5 h-5 text-[var(--text-muted)]" />
          <h2 className="font-semibold">Top 30 clientes por facturación</h2>
        </div>
        <div className="p-5 pt-0 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-[var(--text-muted)] uppercase">
                <th className="text-left  font-semibold pb-2">Cliente</th>
                <th className="text-right font-semibold pb-2">Facturas</th>
                <th className="text-right font-semibold pb-2">Ventas</th>
                <th className="text-right font-semibold pb-2">Año ant. <Leyenda k="vs_anio_anterior" /></th>
                <th className="text-right font-semibold pb-2">Var. <Leyenda k="cambio" /></th>
                <th className="text-right font-semibold pb-2">Margen</th>
                <th className="text-right font-semibold pb-2">Margen% <Leyenda k="margen_bruto" /></th>
                <th className="text-right font-semibold pb-2">Share <Leyenda k="participacion" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]">
              {clientes.map((c, i) => (
                <tr key={c.codigo} className="text-sm hover:bg-[var(--bg-secondary)]">
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-[var(--text-muted)] tabular-nums w-6">#{i + 1}</span>
                      <div className="min-w-0">
                        <p className="font-medium truncate max-w-[280px]">{c.cliente}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {c.codigo}{c.forma_pago ? ` · ${c.forma_pago}` : ''}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmtInt(c.facturas)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">{fmtM(c.ventas)}</td>
                  <td className="py-2 text-right tabular-nums text-[var(--text-muted)]">{c.ventas_prev ? fmtM(c.ventas_prev) : '—'}</td>
                  <td className="py-2 text-right"><Var pct={c.variacion_pct} nuevo={c.nuevo} /></td>
                  <td className="py-2 text-right tabular-nums">{c.margen === null ? '—' : fmtM(c.margen)}</td>
                  <td className={`py-2 text-right tabular-nums font-semibold ${margenTone(c.margen_pct)}`}>
                    {c.margen_pct !== null ? `${c.margen_pct.toFixed(1)}%` : '—'}
                    <SinCosto pct={c.pct_sin_costo} />
                  </td>
                  <td className={`py-2 text-right tabular-nums font-semibold ${c.porcentaje >= 15 ? 'text-[var(--warning)]' : 'text-[var(--text-secondary)]'}`}>
                    {fmtPct(c.porcentaje, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(resumen?.perdidos || []).length > 0 && (
        <div className="card">
          <button className="section-header w-full text-left" onClick={() => setVerPerdidos(v => !v)}>
            <ArrowTrendingDownIcon className="w-5 h-5 text-[var(--danger)]" />
            <h2 className="font-semibold">Clientes que dejaron de comprar ({fmtInt(resumen.n_perdidos)}) · {fmtM(resumen.venta_perdida)} en el período anterior</h2>
            <span className="ml-auto text-xs text-[var(--text-muted)]">{verPerdidos ? 'Ocultar' : 'Ver top 25'}</span>
          </button>
          {verPerdidos && (
            <div className="p-5 pt-0 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-[var(--text-muted)] uppercase">
                    <th className="text-left font-semibold pb-2">Cliente</th>
                    <th className="text-right font-semibold pb-2">Ventas año ant.</th>
                    <th className="text-right font-semibold pb-2">Margen% año ant.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-default)]">
                  {resumen.perdidos.map(c => (
                    <tr key={c.codigo} className="text-sm">
                      <td className="py-2 pr-2">{c.cliente}</td>
                      <td className="py-2 text-right tabular-nums font-semibold text-[var(--danger)]">{fmtM(c.ventas_prev)}</td>
                      <td className="py-2 text-right tabular-nums">{c.margen_pct_prev === null ? '—' : `${c.margen_pct_prev.toFixed(1)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
